import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * Generic sortable list. Call `onReorder(newIds)` whenever the user drops.
 * `items` is an array of objects with at least an `id` field.
 * `renderItem(item, { dragHandleProps, isDragging })` returns JSX for each row.
 */
export function SortableList({ items, onReorder, renderItem, strategy = "vertical", dataTestId }) {
  const sensors = useSensors(
    // Small activation distance so clicks on buttons inside the row still work.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(items, oldIndex, newIndex);
    onReorder(next.map((i) => i.id));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={items.map((i) => i.id)}
        strategy={strategy === "horizontal" ? horizontalListSortingStrategy : verticalListSortingStrategy}
      >
        <div data-testid={dataTestId}>
          {items.map((item) => (
            <SortableRow key={item.id} id={item.id}>
              {(ctx) => renderItem(item, ctx)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };
  const dragHandleProps = { ...attributes, ...listeners };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps, isDragging })}
    </div>
  );
}

/**
 * A reusable grip-handle icon that can be dropped into any sortable row.
 * Spread `dragHandleProps` (from `renderItem` ctx) onto this button.
 */
export function DragHandle({ dragHandleProps, className = "", label = "Drag to reorder" }) {
  return (
    <button
      type="button"
      aria-label={label}
      className={`touch-none cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground -ml-1 px-1 ${className}`}
      {...dragHandleProps}
    >
      <GripVertical className="w-4 h-4" />
    </button>
  );
}
